import * as Sentry from '@sentry/react';
import { isLeft, isRight } from 'fp-ts/Either';
import { lazy, Suspense, useEffect, useState } from 'react';
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useParams,
} from 'react-router';
import { AdminLayout } from './components/AdminLayout';
import { AuthRoute } from './components/AuthRoute';
import { CreateProfilePage } from './components/CreateProfilePage';
import { DappLayout } from './components/DappLayout';
import { DashboardLayout } from './components/DashboardLayout';
import { ClaimSkrPage } from './components/dashboard/ClaimSkrPage';
import { DashboardAlerts } from './components/dashboard/DashboardAlerts';
import { DashboardAnalytics } from './components/dashboard/DashboardAnalytics';
import { DashboardDetails } from './components/dashboard/DashboardDetails';
import { DashboardHome } from './components/dashboard/DashboardHome';
import { DashboardMain } from './components/dashboard/DashboardMain';
import { DashboardQuests } from './components/dashboard/DashboardQuests';
import { DashboardReleases } from './components/dashboard/DashboardReleases';
import { DashboardReviews } from './components/dashboard/DashboardReviews';
import { DeveloperApiKeysPage } from './components/DeveloperApiKeysPage';
import { EmailVerificationPage } from './components/EmailVerificationPage';
import { ForgotPasswordPage } from './components/ForgotPasswordPage';
import { NewDappPage } from './components/NewDappPage';
import { NewVersionPage } from './components/NewVersionPage';
import { NotFoundPage } from './components/NotFoundPage';
import { PreferencesSettingsPage } from './components/PreferencesSettingsPage';
import { ProfileSettingsPage } from './components/ProfileSettingsPage';
import { PublisherAccessPage } from './components/PublisherAccessPage';
import { PublisherInvitePage } from './components/PublisherInvitePage';
import { PublisherProfilePage } from './components/PublisherProfilePage';
import { ReleaseDetailPage } from './components/ReleaseDetailPage';
import { RequiresAuthentication } from './components/RequiresAuthentication';
import { RootRedirect } from './components/RootRedirect';
import { SuperAdminLayout } from './components/SuperAdminLayout';
import { TeamSettingsPage } from './components/TeamSettingsPage';
import { AdminPage } from './pages/AdminPage';
import { AuditManagementPage } from './pages/AuditManagementPage';
import { PersonalizationRunDetailPage } from './pages/appSpotlight/PersonalizationRunDetailPage';
import { QuestClaimsPage } from './pages/quests/QuestClaimsPage';
import { QuestEventsPage } from './pages/quests/QuestEventsPage';
import { DappsPage } from './pages/DappsPage';
import { DatabaseApiPage } from './pages/DatabaseApiPage';
import { DappStatsPage } from './pages/DappStatsPage';
import { DeletedDappsPage } from './pages/DeletedDappsPage';
import { EmailManagementPage } from './pages/EmailManagementPage';
import { HubspotTicketConfigurePage } from './pages/HubspotTicketConfigurePage';
import { HubspotManagementPage } from './pages/HubspotManagementPage';
import { InviteManagementPage } from './pages/InviteManagementPage';
import { KybManagementPage } from './pages/KybManagementPage';
import { MetricsIngestionAdminPage } from './pages/MetricsIngestionAdminPage';
import { ReportingPage } from './pages/ReportingPage';
import { PublishersPage } from './pages/PublishersPage';
import { SuperDappDetailPage } from './pages/SuperDappDetailPage';
import { SuperPublisherDetailPage } from './pages/SuperPublisherDetailPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { useTRPC } from './hooks/useTRPC';
import {
  getAdminDappPath,
  replaceAdminDappRouteSegment,
} from './utils/dappRoutes';

const AppSpotlightPage = lazy(() =>
  import('./pages/AppSpotlightPage').then((module) => ({
    default: module.AppSpotlightPage,
  })),
);
const QuestsPage = lazy(() =>
  import('./pages/quests/QuestsPage').then((module) => ({
    default: module.QuestsPage,
  })),
);
const DappRankingAdminPage = lazy(() =>
  import('./pages/DappRankingAdminPage').then((module) => ({
    default: module.DappRankingAdminPage,
  })),
);
const AiSummariesPage = lazy(() =>
  import('./pages/AiSummariesPage').then((module) => ({
    default: module.AiSummariesPage,
  })),
);

const SubmitKybPage = lazy(() =>
  import('./components/SubmitKybPage').then((module) => ({
    default: module.SubmitKybPage,
  })),
);
const SubmitKycPage = lazy(() =>
  import('./components/SubmitKycPage').then((module) => ({
    default: module.SubmitKycPage,
  })),
);
const SuperPage = lazy(() =>
  import('./pages/SuperPage').then((module) => ({ default: module.SuperPage })),
);

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

function RedirectToDappDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: id ? getAdminDappPath({ id, packageName: null }) : '/dapps',
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

function RedirectToDashboard() {
  const location = useLocation();

  return (
    <Navigate
      to={{
        pathname: '/dashboard',
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

function AdminDappRouteGate() {
  const trpc = useTRPC();
  const location = useLocation();
  const { dappKey } = useParams<{ dappKey: string }>();
  const [dappId, setDappId] = useState<string | null>(null);
  const [redirectPathname, setRedirectPathname] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    setDappId(null);
    setRedirectPathname(null);
    setError(false);

    if (!dappKey) {
      setError(true);
      return;
    }

    void trpc.resolveDappRouteRef
      .query({ ref: dappKey, scope: 'admin' })
      .then((result) => {
        if (isCancelled) {
          return;
        }

        if ('_tag' in result && isLeft(result)) {
          setError(true);
          return;
        }

        if (!('_tag' in result) || !isRight(result)) {
          setError(true);
          return;
        }

        const resolved = result.right;
        const canonicalPathname = replaceAdminDappRouteSegment(
          location.pathname,
          resolved.canonicalSegment,
        );

        if (canonicalPathname !== location.pathname) {
          setRedirectPathname(canonicalPathname);
          return;
        }

        setDappId(resolved.id);
      })
      .catch(() => {
        if (!isCancelled) {
          setError(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [dappKey, location.pathname, trpc]);

  if (redirectPathname) {
    return (
      <Navigate
        to={{
          pathname: redirectPathname,
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  if (error) {
    return <NotFoundPage />;
  }

  if (!dappId) {
    return (
      <div className="flex h-full items-center justify-center text-neutral-400">
        Loading dApp…
      </div>
    );
  }

  return <SuperDappDetailPage resolvedDappId={dappId} />;
}

function DashboardReleaseRedirect() {
  const trpc = useTRPC();
  const location = useLocation();
  const { releaseId } = useParams<{ releaseId: string }>();
  const [redirectPathname, setRedirectPathname] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    setRedirectPathname(null);
    setError(false);

    if (!releaseId) {
      setError(true);
      return;
    }

    void trpc.resolveReleaseRouteRef
      .query({ releaseId })
      .then((result) => {
        if (isCancelled) {
          return;
        }

        if ('_tag' in result && isLeft(result)) {
          setError(true);
          return;
        }

        if (!('_tag' in result) || !isRight(result)) {
          setError(true);
          return;
        }

        setRedirectPathname(result.right.path);
      })
      .catch(() => {
        if (!isCancelled) {
          setError(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [releaseId, trpc]);

  if (redirectPathname) {
    return (
      <Navigate
        to={{
          pathname: redirectPathname,
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    );
  }

  if (error) {
    return <NotFoundPage />;
  }

  return (
    <div className="flex h-full items-center justify-center text-neutral-400">
      Loading release…
    </div>
  );
}

export const routes = [
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '/auth',
    element: <AuthRoute />,
  },
  {
    path: '/login',
    element: <AuthRoute />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/publisher-access',
    element: (
      <RequiresAuthentication>
        <PublisherAccessPage />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/publisher-invite',
    element: <PublisherInvitePage />,
  },
  {
    path: '/create-profile',
    element: (
      <RequiresAuthentication>
        <CreateProfilePage />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <RequiresAuthentication>
        <DashboardLayout />
      </RequiresAuthentication>
    ),
    children: [
      {
        index: true,
        element: <DashboardMain />,
      },
      {
        path: 'home',
        element: <DashboardHome />,
      },
      {
        path: 'analytics',
        element: <DashboardAnalytics />,
      },
      {
        path: 'details',
        element: <RedirectToDashboard />,
      },
      {
        path: 'reviews',
        element: <DashboardReviews />,
      },
      {
        path: 'alerts',
        element: <DashboardAlerts />,
      },
      {
        path: 'claim',
        element: <ClaimSkrPage />,
      },
      {
        path: 'new-dapp',
        element: <NewDappPage />,
      },
      {
        path: 'submit-kyc',
        element: (
          <Suspense fallback={<div>Loading…</div>}>
            <SubmitKycPage />
          </Suspense>
        ),
      },
      {
        path: 'submit-kyb',
        element: (
          <Suspense fallback={<div>Loading…</div>}>
            <SubmitKybPage />
          </Suspense>
        ),
      },
      {
        path: 'settings/profile',
        element: <ProfileSettingsPage />,
      },
      {
        path: 'settings/preferences',
        element: <PreferencesSettingsPage />,
      },
      {
        path: 'settings/api-keys',
        element: <DeveloperApiKeysPage />,
      },
      {
        path: 'settings/team',
        element: <TeamSettingsPage />,
      },
      {
        path: 'settings/publisher',
        element: <PublisherProfilePage />,
      },
    ],
  },
  {
    path: '/dapp/:dappId/stats',
    element: <DappStatsPage />,
  },
  {
    path: '/database-api',
    element: <DatabaseApiPage />,
  },
  {
    path: '/dapp/:dappKey',
    element: (
      <RequiresAuthentication>
        <DashboardLayout />
      </RequiresAuthentication>
    ),
    children: [
      {
        element: <DappLayout />,
        children: [
          {
            path: 'home',
            element: <DashboardHome />,
          },
          {
            path: 'analytics',
            element: <DashboardAnalytics />,
          },
          {
            path: 'releases',
            element: <DashboardReleases />,
          },
          {
            path: 'details',
            element: <DashboardDetails />,
          },
          {
            path: 'reviews',
            element: <DashboardReviews />,
          },
          {
            path: 'quests',
            element: <DashboardQuests />,
          },
          {
            index: true,
            element: <DashboardHome />,
          },
          {
            path: 'new-version',
            element: <NewVersionPage />,
          },
          {
            path: 'release/:releaseId',
            element: <ReleaseDetailPage />,
          },
        ],
      },
    ],
  },
  {
    path: '/releases',
    element: (
      <RequiresAuthentication>
        <RedirectToDashboard />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/analytics',
    element: (
      <RequiresAuthentication>
        <RedirectToDashboard />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/dashboard/releases/:releaseId',
    element: (
      <RequiresAuthentication>
        <DashboardReleaseRedirect />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/verify-email',
    element: (
      <RequiresAuthentication>
        <EmailVerificationPage />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/admin',
    element: (
      <RequiresAuthentication>
        <DashboardLayout />
      </RequiresAuthentication>
    ),
    children: [
      {
        element: <AdminLayout />,
        children: [
          {
            index: true,
            element: <AdminPage />,
          },
          {
            path: 'users',
            element: <UserManagementPage />,
          },
          {
            path: 'kyb',
            element: <KybManagementPage />,
          },
          {
            path: 'invites',
            element: <InviteManagementPage />,
          },
        ],
      },
    ],
  },
  {
    path: '/admin/dapps',
    element: (
      <RequiresAuthentication>
        <Navigate to="/dapps" replace />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/admin/dapps/:id',
    element: (
      <RequiresAuthentication>
        <RedirectToDappDetail />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/super/dapps',
    element: (
      <RequiresAuthentication>
        <Navigate to="/dapps" replace />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/super/dapps/:id',
    element: (
      <RequiresAuthentication>
        <RedirectToDappDetail />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/dapps',
    element: (
      <RequiresAuthentication>
        <DashboardLayout />
      </RequiresAuthentication>
    ),
    children: [
      {
        index: true,
        element: <DappsPage />,
      },
      {
        path: ':dappKey',
        element: <AdminDappRouteGate />,
      },
    ],
  },
  {
    path: '/publishers',
    element: (
      <RequiresAuthentication>
        <DashboardLayout />
      </RequiresAuthentication>
    ),
    children: [
      {
        index: true,
        element: <PublishersPage />,
      },
      {
        path: ':id',
        element: <SuperPublisherDetailPage />,
      },
    ],
  },
  {
    path: '/reporting',
    element: (
      <RequiresAuthentication>
        <DashboardLayout />
      </RequiresAuthentication>
    ),
    children: [
      {
        index: true,
        element: <ReportingPage />,
      },
    ],
  },
  {
    path: '/ai-summaries',
    element: (
      <RequiresAuthentication>
        <DashboardLayout />
      </RequiresAuthentication>
    ),
    children: [
      {
        index: true,
        element: <AiSummariesPage />,
      },
    ],
  },
  {
    path: '/super/ai-summaries',
    element: (
      <RequiresAuthentication>
        <Navigate to="/ai-summaries" replace />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/app-spotlight',
    element: (
      <RequiresAuthentication>
        <DashboardLayout />
      </RequiresAuthentication>
    ),
    children: [
      {
        index: true,
        element: <AppSpotlightPage />,
      },
      {
        path: 'runs/:runId',
        element: <PersonalizationRunDetailPage />,
      },
    ],
  },
  {
    path: '/quests',
    element: (
      <RequiresAuthentication>
        <DashboardLayout />
      </RequiresAuthentication>
    ),
    children: [
      {
        index: true,
        element: <QuestsPage />,
      },
      {
        path: 'claims',
        element: <QuestClaimsPage />,
      },
      {
        path: 'events',
        element: <QuestEventsPage />,
      },
    ],
  },
  {
    path: '/tickets',
    element: (
      <RequiresAuthentication>
        <DashboardLayout />
      </RequiresAuthentication>
    ),
    children: [
      {
        index: true,
        element: <HubspotManagementPage />,
      },
      {
        path: 'configure',
        element: <HubspotTicketConfigurePage />,
      },
    ],
  },
  {
    path: '/admin/hubspot',
    element: (
      <RequiresAuthentication>
        <Navigate to="/tickets" replace />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/super/hubspot',
    element: (
      <RequiresAuthentication>
        <Navigate to="/tickets" replace />
      </RequiresAuthentication>
    ),
  },
  {
    path: '/super',
    element: (
      <RequiresAuthentication>
        <DashboardLayout />
      </RequiresAuthentication>
    ),
    children: [
      {
        element: <SuperAdminLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<div>Loading…</div>}>
                <SuperPage />
              </Suspense>
            ),
          },
          {
            path: 'users',
            element: <UserManagementPage />,
          },
          {
            path: 'deleted-dapps',
            element: <DeletedDappsPage />,
          },
          {
            path: 'kyb',
            element: <KybManagementPage />,
          },
          {
            path: 'emails',
            element: <EmailManagementPage />,
          },
          {
            path: 'invites',
            element: <InviteManagementPage />,
          },
          {
            path: 'audit',
            element: <AuditManagementPage />,
          },
          {
            path: 'metrics-ingestion',
            element: <MetricsIngestionAdminPage />,
          },
          {
            path: 'dapp-rankings',
            element: <DappRankingAdminPage />,
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export const router = sentryCreateBrowserRouter(routes);
